<script setup lang="ts">
import type { AutoCompleteCompleteEvent } from 'primevue/autocomplete'
import type { SalesDemoSource } from '@swipe4work/models-orval'
import { sourceOptions, sourceOtherSuggestions } from '@/features/sales/composables/useSalesMetricsAdapter'

/*
 * The channel a demo came in through: a fixed list plus an "Anders" option that asks for the channel
 * itself, with the channels we see most often offered as suggestions.
 */
const props = defineProps<{
  source: SalesDemoSource | null
  sourceOther: string
  idSuffix?: string
  sourceError?: string
  sourceOtherError?: string
}>()

const emit = defineEmits<{
  (e: 'update:source', v: SalesDemoSource | null): void
  (e: 'update:sourceOther', v: string): void
}>()

const sourceId = computed(() => [ 'demoSource', props.idSuffix ].filter(Boolean).join('-'))
const sourceOtherId = computed(() => [ 'demoSourceOther', props.idSuffix ].filter(Boolean).join('-'))

const selectedSource = computed({
  get: () => props.source,
  set: (v: SalesDemoSource | null) => emit('update:source', v),
})

const otherChannel = computed({
  get: () => props.sourceOther,
  set: (v: string) => emit('update:sourceOther', v ?? ''),
})

const suggestions = ref<string[]>([ ...sourceOtherSuggestions ])

function searchSuggestions({ query }: AutoCompleteCompleteEvent) {
  const term = query.trim().toLowerCase()

  suggestions.value = term
    ? sourceOtherSuggestions.filter(s => s.toLowerCase().includes(term))
    : [ ...sourceOtherSuggestions ]
}
</script>

<template>
  <div class="col-span-2 flex flex-col gap-1">
    <label :for="sourceId">Bron</label>
    <Select
      v-model="selectedSource"
      :input-id="sourceId"
      :options="sourceOptions"
      option-label="label"
      option-value="value"
      placeholder="Selecteer bron"
      :invalid="!!sourceError"
      class="w-full"
    />
    <Message v-if="sourceError" severity="error" size="small" variant="simple">
      {{ sourceError }}
    </Message>
  </div>

  <div v-if="selectedSource === 'Other'" class="col-span-2 flex flex-col gap-1">
    <label :for="sourceOtherId">Welk kanaal?</label>
    <AutoComplete
      v-model="otherChannel"
      :input-id="sourceOtherId"
      :suggestions="suggestions"
      dropdown
      complete-on-focus
      placeholder="Bijv. LinkedIn"
      :invalid="!!sourceOtherError"
      fluid
      @complete="searchSuggestions"
    />
    <Message v-if="sourceOtherError" severity="error" size="small" variant="simple">
      {{ sourceOtherError }}
    </Message>
  </div>
</template>
